import React, { useState, useEffect } from 'react';
import { SchemaGenerateRequest, QuestionCategory } from '../types';
import { adminFormsService } from '../services/admin-forms.service';
import { api } from '../../../../core/utils/apiClient';
import { COLORS } from '../../../../core/constants/colors';

interface GenerateSchemaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES: QuestionCategory[] = ['basic_info', 'financial', 'operational', 'strategic', 'compliance', 'other'];

export const GenerateSchemaDrawer: React.FC<GenerateSchemaDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<SchemaGenerateRequest>({
    company_id: 0,
    document_ids: [],
    categories: [],
  });
  const [companies, setCompanies] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.company_id) {
      loadDocuments(formData.company_id);
    } else {
      setDocuments([]);
    }
  }, [formData.company_id]);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/v1/companies/');
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadDocuments = async (companyId: number) => {
    try {
      const response = await api.get(`/api/v1/documents/companies/${companyId}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.company_id) {
      setError('Please select a company');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await adminFormsService.generateSchema(formData);
      if (result) {
        onSuccess();
        onClose();
        // Reset form
        setFormData({
          company_id: 0,
          document_ids: [],
          categories: [],
        });
        setDocuments([]);
      } else {
        setError('Failed to generate schema');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDocument = (docId: number) => {
    setFormData(prev => ({
      ...prev,
      document_ids: prev.document_ids?.includes(docId)
        ? prev.document_ids.filter(id => id !== docId)
        : [...(prev.document_ids || []), docId]
    }));
  };

  const toggleCategory = (category: QuestionCategory) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories?.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...(prev.categories || []), category]
    }));
  };

  const handleDiscard = () => {
    setFormData({
      company_id: 0,
      document_ids: [],
      categories: [],
    });
    setDocuments([]);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleDiscard}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Generate Schema Using RAG
            </h2>
            <button
              onClick={handleDiscard}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 px-6 py-6 overflow-y-auto">
            {error && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Company *
                </label>
                <select
                  value={formData.company_id || ''}
                  onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value), document_ids: [] })}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <option value="">Select a company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              {formData.company_id > 0 && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                      Select Documents (Optional) ({formData.document_ids?.length || 0} selected)
                    </label>
                    <div className="border rounded-lg p-4 max-h-48 overflow-y-auto" style={{ borderColor: '#E5E7EB' }}>
                      {documents.length === 0 ? (
                        <p className="text-sm" style={{ color: '#9CA3AF' }}>No documents available for this company</p>
                      ) : (
                        <div className="space-y-2">
                          {documents.map((doc) => (
                            <label
                              key={doc.id}
                              className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.document_ids?.includes(doc.id) || false}
                                onChange={() => toggleDocument(doc.id)}
                                className="mt-1 w-4 h-4 rounded"
                                style={{ accentColor: COLORS.admin.primary }}
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium" style={{ color: '#1F2937' }}>
                                  {doc.original_file_name || doc.file_name}
                                </p>
                                <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                                  {new Date(doc.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                      If no documents are selected, all documents for the company will be analyzed
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                      Categories (Optional) ({formData.categories?.length || 0} selected)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map((category) => (
                        <label
                          key={category}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:bg-gray-50 transition-all"
                          style={{ borderColor: '#E5E7EB' }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.categories?.includes(category) || false}
                            onChange={() => toggleCategory(category)}
                            className="w-4 h-4 rounded"
                            style={{ accentColor: COLORS.admin.primary }}
                          />
                          <span className="text-sm capitalize" style={{ color: '#1F2937' }}>
                            {category.replace('_', ' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                      If no categories are selected, all categories will be used
                    </p>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="pt-6 space-y-3">
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: COLORS.admin.primary }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Generating...' : 'Generate Schema'}
                </button>
                <button
                  type="button"
                  onClick={handleDiscard}
                  className="w-full py-2 text-sm font-medium hover:underline"
                  style={{ color: '#6B7280' }}
                  disabled={isSubmitting}
                >
                  Discard changes
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

